/**
 * One main button and one rule (Brandy and Dustin, October 5, 2026; design study
 * scratch/single-confirm, label option "r").
 *
 * The main button confirms the whole document and moves on to the next document asked about,
 * in list order, wrapping (one list since October 30, 2026: every-document-calibration.md). Its
 * words follow the document: "Confirm & next" when there is something to confirm, "Next" when it
 * is already confirmed, "Confirm" on the last document asked about. Ctrl+Enter does exactly what
 * the button says, from anywhere on the
 * review screen. There are no partial confirms any more: the whole-document confirmation
 * already saves every answer they used to (each value, the type, the start and the table).
 */
import { lineItemArithmetic } from '../shared/line-item-arithmetic';
import type { TelemetryUiErrorCode } from '../shared/telemetry-catalog';

/** How long "Done ✓" shows on the page before the next document opens. */
export const CONFIRM_BEAT_MS = 520;
/** How long "✓ Confirmed · Back" stays in the bar after a move. */
export const BACK_OFFER_MS = 6000;
/**
 * A press or click this soon after the review moved on by itself (Next, the move after Done, Skip,
 * Back, Ctrl+PgDn) or re-read the open document is not a look at it: a double-click
 * or a double press must never confirm a document nobody has seen.
 */
export const ARRIVAL_GUARD_MS = 350;

export type MainActionKind = 'confirm-next' | 'confirm' | 'next' | 'confirmed';
export type MainAction = { kind: MainActionKind; label: string; next: number | null };

/** What the main button (and Ctrl+Enter) does on this document. */
export function mainReviewAction(confirmed: boolean, next: number | null): MainAction {
  if (confirmed)
    return next == null
      ? { kind: 'confirmed', label: 'Document confirmed', next: null }
      : { kind: 'next', label: 'Next', next };
  return next == null
    ? { kind: 'confirm', label: 'Confirm', next: null }
    : { kind: 'confirm-next', label: 'Confirm & next', next };
}

type Row = {
  rowNumber: number;
  quantity?: number | null;
  unitPrice?: number | null;
  amount?: number | null;
  arithmeticValidated?: boolean;
};

/**
 * The first line whose amount question is still open: quantity × unit price doesn't make the
 * amount and nobody has said "Looks right" or used the sum. Exactly the rows that show "Check
 * amount" (LineItemCheck), so the button refuses only for a question on screen. A line whose
 * numbers agree but carry no check (an AI reviewer's unchecked row, an old sign repair) is no
 * question: the confirmation records that its numbers agree (`agreeingUncheckedRows`).
 */
export const openMathRow = (rows: readonly Row[]): Row | undefined =>
  rows.find(
    (row) =>
      row.quantity != null &&
      row.unitPrice != null &&
      row.amount != null &&
      !row.arithmeticValidated &&
      lineItemArithmetic(row).verdict === 'off',
  );

/**
 * Lines whose quantity × unit price makes the amount (or its credit) but that carry no check.
 * Confirm table and the row circles used to set that check; the whole-document confirmation now
 * does, for these lines only, as the same line-item answer. That the numbers agree is a fact
 * anyone can work out (line-item-arithmetic.ts, arithmeticValidatedAfterEdit), never a judgement
 * about a line that doesn't add up: those keep their own question.
 */
export const agreeingUncheckedRows = <T extends Row>(rows: readonly T[]): T[] =>
  rows.filter(
    (row) =>
      row.quantity != null &&
      row.unitPrice != null &&
      row.amount != null &&
      !row.arithmeticValidated &&
      lineItemArithmetic(row).verdict !== 'off',
  );

/**
 * Why the main button can't confirm this document yet, in one plain line, with its reason as a code word for
 * telemetry (`review.confirm-refused:<code>`, message `confirm-<code>`); null when it can.
 */
export function confirmRefusal({
  rows,
  missingRequired,
  notRead,
  typeChosen,
  typeRenamed = false,
}: {
  rows: readonly Row[];
  missingRequired?: string;
  notRead: boolean;
  typeChosen: boolean;
  /**
   * Filed under a name its type id's type never had (a learned lesson's name, another type's).
   * A name the type had before a rename is that type (document-type-identity.ts), not this.
   */
  typeRenamed?: boolean;
}): { message: string; code: ConfirmRefusalReason; lineRow?: number; field?: string } | null {
  if (!typeChosen)
    return {
      message: typeRenamed
        ? 'Choose the correct document type first'
        : 'Choose a document type first',
      code: 'no-type',
    };
  if (notRead) return { message: "This document isn't fully read yet", code: 'not-read' };
  const math = openMathRow(rows);
  if (math)
    return {
      message: `Check line ${math.rowNumber}'s amount first`,
      code: 'line-amount',
      lineRow: math.rowNumber,
    };
  if (missingRequired)
    return {
      message: `Fill in ${missingRequired} first`,
      code: 'field-required',
      field: missingRequired,
    };
  return null;
}

/** The reasons `confirmRefusal` gives. */
export type ConfirmRefusalReason = 'no-type' | 'not-read' | 'line-amount' | 'field-required';
/** The message code of each refusal's toast (telemetry-catalog `telemetryUiErrorCodes`): sent instead of its words. */
export const confirmRefusalMessageCode = {
  'no-type': 'confirm-no-type',
  'not-read': 'confirm-not-read',
  'line-amount': 'confirm-line-amount',
  'field-required': 'confirm-field-required',
} as const satisfies Record<ConfirmRefusalReason, TelemetryUiErrorCode>;

const OPEN_WINDOWS =
  '[aria-modal="true"], dialog[open], [role="alertdialog"], [role="menu"], .job-selector-popup, .simulate-scanner-ask';

/**
 * Whether a window, a question or a menu is open over the screen. The review screen's keys never
 * act behind one: PgDn, Ctrl+B, Ctrl+F, Ctrl+Z and the zoom keys used to reach the document under
 * the F1 sheet, and Ctrl+B there split it (shortcuts audit, 2026-10-07).
 */
export const anyWindowOpen = (root: ParentNode = document): boolean =>
  Boolean(root.querySelector(OPEN_WINDOWS));

/**
 * Whether Ctrl+Enter pressed here belongs to the review screen. Anywhere on it works: a field,
 * a table cell, the page, the documents list, a tab or a button just clicked (focus left on the
 * page after a move used to swallow the second press). Never from an open window (the
 * shortcuts sheet, document search, a settings window), Job setup, or a text box that isn't one
 * of the document's own values (the page's Find box, a new type or field name): unscoped, the
 * shortcut once fired from the search box and wrote false ground truth (adversarial review).
 */
export function reviewShortcutTarget(
  target: EventTarget | null,
  root: Document = document,
): boolean {
  // Windows and anything asking a question first. The page's small text-selection menu is not
  // one: it opens on its own when words are selected, and must not swallow the shortcut.
  if (anyWindowOpen(root)) return false;
  if (!(target instanceof Element)) return true;
  if (target.closest('[inert]')) return false;
  const editable = target.closest("input, textarea, select, [contenteditable='true']");
  if (!editable) return true;
  if (editable.closest('.inline-add-field, .add-type-inline')) return false;
  return Boolean(
    editable.closest('.results-panel, .line-item-ledger, .detached-fields, .detached-lines'),
  );
}
